import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import ScrollToTop from "@/components/layout/ScrollToTop";

// Singleton QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60000,
      retry: 1,
    },
  },
});

// Route-level code splitting: every route loads as its own chunk so marketing
// visitors never download the app dashboard (recharts, react-day-picker, the
// full Radix set) and app users never download the marketing surface.
// Shared dependencies are hoisted into common chunks by Vite automatically.

// Marketing (Radiant/Studio — self-contained chrome)
// Index is imported eagerly on purpose: `/` is the highest-traffic entry and
// a lazy chunk adds a serial network hop before the hero (LCP) can paint.
// Its component dependencies (radiant/, framer-motion, helmet) are shared by
// every other marketing route, so the entry-size cost is small.
import Index from "@/pages/Index";
const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Contact = lazy(() => import("@/pages/Contact"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const Refunds = lazy(() => import("@/pages/Refunds"));
const Status = lazy(() => import("@/pages/Status"));
const Login = lazy(() => import("@/pages/Login"));
const Enterprise = lazy(() => import("@/pages/Enterprise"));
const Security = lazy(() => import("@/pages/Security"));
const Platform = lazy(() => import("@/pages/Platform"));
const NotFound = lazy(() => import("@/pages/NotFound"));

// Auth / public utility
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const VendorUploadPage = lazy(() => import("@/pages/public/VendorUploadPage"));

// Legacy marketing chrome routes
const MarketingLayout = lazy(() => import("@/layout/MarketingLayout"));
const StatusHTML = lazy(() => import("@/pages/StatusHTML"));
const CheckoutSuccess = lazy(() => import("@/pages/CheckoutSuccess"));
const CheckoutCancel = lazy(() => import("@/pages/CheckoutCancel"));
const ThankYou = lazy(() => import("@/pages/ThankYou"));
const Form = lazy(() => import("@/pages/Form"));

// Onboarding wizard
const OnboardingLayout = lazy(() => import("@/pages/onboarding/OnboardingLayout"));
const StepCompany = lazy(() => import("@/pages/onboarding/StepCompany"));
const StepRequirements = lazy(() => import("@/pages/onboarding/StepRequirements"));
const StepVendors = lazy(() => import("@/pages/onboarding/StepVendors"));
const StepNotifications = lazy(() => import("@/pages/onboarding/StepNotifications"));
const StepIntegrations = lazy(() => import("@/pages/onboarding/StepIntegrations"));
const StepBranding = lazy(() => import("@/pages/onboarding/StepBranding"));
const StepReview = lazy(() => import("@/pages/onboarding/StepReview"));
const OnboardingComplete = lazy(() => import("@/pages/onboarding/OnboardingComplete"));

// App dashboard
const AppLayout = lazy(() => import("@/layout/AppLayout"));
const Dashboard = lazy(() => import("@/pages/app/Dashboard"));
const AppDashboard = lazy(() => import("@/pages/app/AppDashboard"));
const VendorsTab = lazy(() => import("@/pages/app/VendorsTab"));
const VendorDetail = lazy(() => import("@/pages/app/VendorDetail"));
const CertificatesTab = lazy(() => import("@/pages/app/CertificatesTab"));
const DocumentsInbox = lazy(() => import("@/pages/app/DocumentsInbox"));
const AlertsTab = lazy(() => import("@/pages/app/AlertsTab"));
const ReportsTab = lazy(() => import("@/pages/app/ReportsTab"));
const SettingsTab = lazy(() => import("@/pages/app/SettingsTab"));

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={0}>
        <BrowserRouter>
          <ScrollToTop />
          {/* Blank full-height fallback: avoids layout shift while a route
              chunk loads (matches the previous IndexLazy behavior). */}
          <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
            <Routes>
              {/* AUTH CALLBACK MUST BE TOP-LEVEL - both with and without trailing slash */}
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/auth/callback/" element={<AuthCallback />} />

              {/* Vendor upload portal - public, no auth */}
              <Route path="/upload/:token" element={<VendorUploadPage />} />

              {/* Accept team invite - auth required */}
              <Route path="/accept-invite" element={<AcceptInvite />} />

              {/* Enterprise Onboarding Wizard - No header/footer */}
              <Route path="/onboarding" element={<OnboardingLayout />}>
                <Route path="company" element={<StepCompany />} />
                <Route path="requirements" element={<StepRequirements />} />
                <Route path="vendors" element={<StepVendors />} />
                <Route path="notifications" element={<StepNotifications />} />
                <Route path="integrations" element={<StepIntegrations />} />
                <Route path="branding" element={<StepBranding />} />
                <Route path="review" element={<StepReview />} />
                <Route path="complete" element={<OnboardingComplete />} />
              </Route>

              {/* App Dashboard - With sidebar */}
              <Route element={<AppLayout />}>
                <Route path="/app" element={<AppDashboard />} />
                <Route path="/app/dashboard" element={<Dashboard />} />
                <Route path="/app/vendors" element={<VendorsTab />} />
                <Route path="/app/vendors/:vendorId" element={<VendorDetail />} />
                <Route path="/app/certificates" element={<CertificatesTab />} />
                <Route path="/app/documents" element={<DocumentsInbox />} />
                <Route path="/app/alerts" element={<AlertsTab />} />
                <Route path="/app/reports" element={<ReportsTab />} />
                <Route path="/app/settings" element={<SettingsTab />} />
              </Route>

              {/* REBUILT MARKETING (Radiant/Studio — self-contained chrome, no MarketingLayout) */}
              <Route path="/" element={<Index />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/security" element={<Security />} />
              <Route path="/platform" element={<Platform />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/enterprise" element={<Enterprise />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/refunds" element={<Refunds />} />
              <Route path="/login" element={<Login />} />
              <Route path="/status" element={<Status />} />
              <Route path="/thank-you" element={<ThankYou />} />

              {/* MARKETING SITE (legacy chrome via MarketingLayout — migrated batch by batch) */}
              <Route element={<MarketingLayout />}>
                <Route path="/status-html" element={<StatusHTML />} />
                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                <Route path="/checkout/cancel" element={<CheckoutCancel />} />
                <Route path="/form" element={<Form />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
}
